/**
 * AccountsTab Component
 * Year-specific accounts and commission data entry form
 */
import React, { memo } from 'react';
import { Button } from '../ui/button';
import { Save, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { SalesField, DateField } from './SalesTab';
import { SelectField } from './FieldOptions';

export const AccountsTab = memo(({ 
  salesYear,
  salesData, 
  salesLoading, 
  salesDirty, 
  savingSales, 
  saveSalesData, 
  updateSalesFormRef 
}) => {
  if (salesLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4" key={`accts-${salesData.org_id || 'unknown'}-${salesYear}-${salesData.sales_id || 'new'}`}>
      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          size="sm" 
          onClick={saveSalesData}
          disabled={!salesDirty || savingSales}
          className={cn(salesDirty && "animate-pulse")}
        >
          {savingSales ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
          {salesDirty ? 'Save Changes' : 'Saved'}
        </Button>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-1 bg-purple-50/50 dark:bg-purple-950/20 rounded-lg p-3">
          <h5 className="text-xs font-semibold text-purple-700 dark:text-purple-400 mb-2 uppercase tracking-wide">Income & Orders</h5>
          <SalesField label="# Envelopes Received" field="num_of_envelopes_received" defaultValue={salesData.num_of_envelopes_received} onFieldChange={updateSalesFormRef} />
          <SalesField label="Customer Rating" field="customer_rating" defaultValue={salesData.customer_rating} onFieldChange={updateSalesFormRef} />
          <SalesField label="ABN" field="abn" defaultValue={salesData.abn} onFieldChange={updateSalesFormRef} />
          <SalesField label="Income Received" field="income_received" defaultValue={salesData.income_received} onFieldChange={updateSalesFormRef} />
        </div>
        <div className="space-y-1 bg-amber-50/50 dark:bg-amber-950/20 rounded-lg p-3">
          <h5 className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-2 uppercase tracking-wide">Commission</h5>
          <SelectField label="Commission Required" field="commission_required" defaultValue={salesData.commission_required} onFieldChange={updateSalesFormRef} />
          <DateField label="Claim Form Received" field="claim_form_received" defaultValue={salesData.claim_form_received} onFieldChange={updateSalesFormRef} />
          <SalesField label="Commission Amount" field="commission_amount" defaultValue={salesData.commission_amount} onFieldChange={updateSalesFormRef} />
          <DateField label="Commission Sent Date" field="commission_sent_date" defaultValue={salesData.commission_sent_date} onFieldChange={updateSalesFormRef} />
        </div>
      </div>
    </div>
  );
});

AccountsTab.displayName = 'AccountsTab';

export default AccountsTab;
